import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import { usePermissions } from './usePermissions';
import { toast } from 'sonner';

type SecureMutationOptions<TData, TError, TVariables> = {
  requiredPermission?: string;
  requiresAuth?: boolean;
  onUnauthorized?: () => void;
  rateLimitKey?: string;
  rateLimitWindow?: number; // in milliseconds
  rateLimitMax?: number;
} & Omit<UseMutationOptions<TData, TError, TVariables>, 'mutationFn'>;

const rateLimitStore = new Map<string, { count: number; resetTime: number }>();

const checkRateLimit = (key: string, window: number, max: number): boolean => {
  const now = Date.now();
  const current = rateLimitStore.get(key);
  
  if (!current || now > current.resetTime) {
    rateLimitStore.set(key, { count: 1, resetTime: now + window });
    return true;
  }
  
  if (current.count >= max) {
    return false;
  }
  
  current.count++;
  return true;
};

export const useSecureMutation = <TData, TError, TVariables>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  options: SecureMutationOptions<TData, TError, TVariables> = {}
) => {
  const { user, hasPermission } = usePermissions();
  
  const {
    requiredPermission,
    requiresAuth = true,
    onUnauthorized,
    rateLimitKey,
    rateLimitWindow = 60000, // 1 minute default
    rateLimitMax = 10, // 10 requests per minute default
    ...mutationOptions
  } = options;

  const isAuthorized = () => {
    if (requiresAuth && !user) return false;
    if (requiredPermission && !hasPermission(requiredPermission)) return false;
    return true;
  };

  return useMutation({
    ...mutationOptions,
    mutationFn: async (variables: TVariables) => {
      if (!isAuthorized()) {
        onUnauthorized?.();
        toast.error('Unauthorized access');
        throw new Error('Unauthorized access');
      }

      if (rateLimitKey && !checkRateLimit(rateLimitKey, rateLimitWindow, rateLimitMax)) {
        toast.error('Too many requests. Please try again later.');
        throw new Error('Rate limit exceeded');
      }

      return mutationFn(variables);
    }
  });
};